import React from 'react';
import { Upload, Box, Headset, Palette, Calculator, Download } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: Upload,
      title: 'Upload Your Space',
      description: 'Take photos of your room or upload floor plans. Our AI instantly creates a 3D model you can work with.',
      image: 'https://images.pexels.com/photos/262367/pexels-photo-262367.jpeg',
    },
    {
      icon: Box,
      title: 'Visualize in AR',
      description: 'Use your phone to see your redesigned space in augmented reality. Walk through it, make changes in real-time.',
      image: 'https://images.pexels.com/photos/1109541/pexels-photo-1109541.jpeg',
    },
    {
      icon: Palette,
      title: 'Design & Customize',
      description: 'Try different furniture, colors, and layouts. Our AI suggests options that match your style and budget.',
      image: 'https://images.pexels.com/photos/32913099/pexels-photo-32913099.jpeg',
    },
    {
      icon: Calculator,
      title: 'Get Instant Pricing',
      description: 'See exact costs for everything. No hidden fees, no surprises. Compare prices from verified partners.',
      image: 'https://images.pexels.com/photos/9040480/pexels-photo-9040480.jpeg',
    },
    {
      icon: Headset,
      title: 'Shop Everything',
      description: 'Order furniture, materials, and services directly through the platform. Everything you need in one place.',
      image: 'https://images.pexels.com/photos/3764550/pexels-photo-3764550.jpeg',
    },
    {
      icon: Download,
      title: 'Get It Installed',
      description: 'Our trusted partners handle delivery and installation. Track everything from your phone.',
      image: 'https://images.pexels.com/photos/34840277/pexels-photo-34840277.jpeg',
    },
  ];

  return (
    <section id="how-it-works" className="relative py-32 px-6 md:px-[7.6923%]" style={{
      background: 'linear-gradient(to bottom, #FFFFFF 0%, #E6F2FF 50%, #FFFFFF 100%)'
    }}>
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%), repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)',
          backgroundSize: '100% 100%',
          opacity: 0.03,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-20">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-6"
            style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            From Idea to Reality in 6 Simple Steps
          </h2>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            No contractors to worry about. No design mistakes. No wasted money. Just your dream home, exactly as you imagined it.
          </p>
        </div>

        {/* Steps timeline */}
        <div className="space-y-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              } gap-8 md:gap-12 items-center`}
            >
              {/* Content */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 flex items-center justify-center rounded-2xl"
                    style={{
                      borderColor: 'var(--brand-primary)',
                      background: 'linear-gradient(135deg, rgba(255,128,0,0.15) 0%, rgba(255,215,0,0.15) 100%)',
                    }}
                  >
                    <step.icon size={32} color="#FF8000" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div
                      className="text-sm font-medium mb-1"
                      style={{ color: 'var(--brand-primary)' }}
                    >
                      STEP {index + 1}
                    </div>
                    <h3
                      className="text-3xl md:text-4xl font-semibold"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {step.title}
                    </h3>
                  </div>
                </div>
                <p
                  className="text-lg md:text-xl"
                  style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
                >
                  {step.description}
                </p>
              </div>

              {/* Image */}
              <div className="flex-1 relative group">
                <div className="relative overflow-hidden rounded-3xl shadow-lg">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-[350px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 border-2 transition-opacity duration-400 opacity-0 group-hover:opacity-100"
                    style={{ borderColor: 'var(--brand-primary)' }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <a href="#register">
            <button className="bg-[#FF8000] text-black border-none rounded-2xl px-8 py-4 text-lg font-medium cursor-pointer transition-all duration-400 hover:bg-[rgba(0,255,209,0.1)] hover:text-[#FF8000] min-h-[56px] flex items-center justify-center gap-3 mx-auto">
              Register Your Interest
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;